import { useQueryClient } from '@tanstack/react-query';
import {
  type OptimisticMutationOptions,
  useOptimisticMutation,
} from '../../../hooks/use-optimistic-mutation';
import { transformSettingsToApiResponse } from '../transformers';
import type { CustomerGroupBuilderValidated } from '../types/customer-group.schema';
import type { CustomerGroupApiResponse } from '../types/customer-group-response';
import { QK_GET_CUSTOMER_GROUPS } from './use-customer-groups';
import { useCustomerGroupsApi } from './use-customer-groups-api';

export function useCreateCustomerGroupWithSettings(
  options?: OptimisticMutationOptions<CustomerGroupApiResponse[], CustomerGroupBuilderValidated>,
) {
  const { customerGroupsApi } = useCustomerGroupsApi();
  const queryClient = useQueryClient();

  return useOptimisticMutation<CustomerGroupApiResponse[], CustomerGroupBuilderValidated>({
    ...options,
    queryClient,
    queryKey: QK_GET_CUSTOMER_GROUPS(),
    mutationFn: async (group) => {
      await customerGroupsApi.group(group.slug).create(group);

      try {
        await customerGroupsApi.group(group.slug).settings().update(group.settings);
      } catch (err) {
        // If settings update failed, we want the whole "transaction" to fail - roll back by
        // deleting the customer group
        await customerGroupsApi.group(group.slug).delete();
        throw err;
      }
    },
    optimisticUpdate: (prev, newGroup) => {
      const transformedGroup = {
        ...newGroup,
        settings: transformSettingsToApiResponse(newGroup.settings),
      };
      return prev ? [...prev, transformedGroup] : [transformedGroup];
    },
  });
}
